import { post } from '../app/api'
import { get, reverse } from 'lodash'
import { authStore, teamStore, billingStore } from '../app/stores'
import { observable, action, computed, toJS } from 'mobx'
import { firebase, SESSION_ENDPOINT, SETTINGS_ENDPOINT, REFRESH_AVATAR_ENDPOINT, AVATAR_CONFIG } from '../app/constants'
import moment from 'moment'

class ChannelStore {
  @observable channels = []
  @observable channel = null
  @observable analytics = null
  @observable error = null
  @observable contacts = null
  @observable refreshingAvatar = false

  @computed get channelId () {
    return get(this.channel, 'slackData.channel.id')
  }

  @computed get appearanceSettings () {
    const settings = get(this.channel, 'settings.appearance')
    return settings ? toJS(settings) : {}
  }

  @computed get behaviorSettings () {
    const settings = get(this.channel, 'settings.behavior')
    return settings ? toJS(settings) : {}
  }

  @computed get autoMessages () {
    const autoMessages = get(this.channel, 'settings.automessages', {})
    return reverse(Object.keys(autoMessages)).map(key => {
      return { ...autoMessages[key], key }
    })
  }

  @computed get isArchived () {
    return teamStore.team && teamStore.team.archivedChannels && teamStore.team.archivedChannels[this.channelId]
  }

  @action.bound loadContacts(channelId){
    firebase.database()
      .ref(`customDataList/${channelId}`)
      .once('value')
      .then(action((snapshot)=>{
        if(snapshot.val()){
          console.log("customData records: " + Object.values(snapshot.val()).length)
          console.log("customData records with emails: " + Object.values(snapshot.val()).filter(a => a.contact || a.email).length)
          var duplicates = 0
          this.contacts = Object.values(snapshot.val())
            .sort((a,b) => moment(b.dateCreated).unix() - moment(a.dateCreated).unix())
            .filter(a => a.contact || a.email)
            .reduce((contacts, contact) => {
              if(contacts[contact.email || contact.contact]){
                duplicates++
              }
              contacts[contact.email || contact.contact] = contact
              return contacts
            }, {})
            console.log("duplicates: " + duplicates)
          console.log("unique records: " + Object.values(this.contacts).length)
        }
      }))
      .catch(action(err => {
        this.error = err
      }))
  }

  @action.bound createChannel (name) {
    return post(SESSION_ENDPOINT, {
      type: 'createChannel',
      channelName: name,
      uid: authStore.currentUser.uid
    })
    .then(res => res.json())
    .then(res => {
      // NOTE: result will always come back with .errorMessage even if successful
      const { success, error, newChannelKey } = JSON.parse(res.errorMessage)
      if (error) {
        return Promise.reject(error)
      } else if (success) {
        firebase.database()
          .ref(`scChannel/${newChannelKey}`)
          .once('value', snap => {
            const channel = snap.val()
            const channelId = get(channel, 'slackData.channel.id')
            this.updateScript(channel, channelId, newChannelKey)
          })
        return Promise.resolve(newChannelKey)
      }
    })
    .catch(action(err => {
      this.error = err
    }))
  }

  @action.bound listenToChannel (key) {
    if (this.channel) { this.unlistenToChannel() }
    firebase.database()
      .ref(`scChannel/${key}`)
      .on('value', action(snap => {
        const channel = snap.val()
        if (channel) {
          this.channel = channel
          this.channel.key = snap.key
          this.ensureProSettings().then(updates => {
            if (updates.length) {
              this.updateScript()
            }
          })
        }
      }))
  }

  @action.bound clearError () {
    this.error = null
  }

  @action.bound unlistenToChannel () {
    firebase.database()
      .ref(`scChannel/${this.channel.key}`)
      .off('value')
    this.channel = null
    this.contacts = null
  }

  @action.bound saveSettings (key, data) {
    console.log("saveSettings", key, data)
    return firebase.database()
      .ref(`scChannel/${this.channel.key}/settings/${key}`)
      .set(data)
      .then(this.ensureProSettings)
      .then(this.updateScript)
  }

  @action.bound addAutoMessage (data) {
    const autoMessage = Object.assign({}, data, { user: authStore.userId })
    return firebase.database()
      .ref(`scChannel/${this.channel.key}/settings/automessages`)
      .push(autoMessage)
      .then(this.updateScript)
  }

  @action.bound removeAutoMessage (key) {
    return firebase.database()
      .ref(`scChannel/${this.channel.key}/settings/automessages/${key}`)
      .remove()
      .then(this.updateScript)
  }

  @action.bound editAutoMessage (key, data) {
    const autoMessage = Object.assign({}, data, { user: authStore.userId })
    return firebase.database()
      .ref(`scChannel/${this.channel.key}/settings/automessages/${key}`)
      .set(autoMessage)
      .then(this.updateScript)
  }

  // runs whenever channel page is loaded or channel settings are saved
  @action.bound ensureProSettings () {
    let updates = []
    // need to delete pro level settings and generate script again
    // NOTE: cannot depend on access values before team has loaded.
    // this is the case when the channel page is directly routed to
    // so we play it safe and abort.
    if (teamStore.team) {
      if (!billingStore.hasWhiteLabelAccess) {
        if (this.appearanceSettings.hide_logo) {
          updates.push(
            firebase.database()
            .ref(`scChannel/${this.channel.key}/settings/appearance/hide_logo`)
            .remove()
          )
        }
      }
      if (!billingStore.hasAutoMessagesAccess) {
        // FIXME: what should we do w/ automessages?
        // if (this.autoMessages.length) {
        //   updates.push(
        //     firebase.database()
        //     .ref(`scChannel/${this.channel.key}/settings/automessages`)
        //     .remove()
        //   )
        // }
      }
    }
    return Promise.all(updates)
  }

  @action.bound updateScript (updates, id, key) {
    // purposely don't return here so
    // invoking methods can resolve early.
    post(SETTINGS_ENDPOINT, {
      slackChannelId: id || this.channelId,
      slackTeamId: teamStore.teamId,
      scChannelId: key || this.channel.key,
      uid: authStore.currentUser.uid
    }).catch(e => e)
  }

  @action.bound refreshAvatar (avatarConfig) {
    this.refreshingAvatar = true
    post(REFRESH_AVATAR_ENDPOINT, {
      scUserId: authStore.scUserId,
      id: Number(avatarConfig) === AVATAR_CONFIG.TEAM ? teamStore.teamId : authStore.userId
    }).catch(err => {})
      .then(action(() => {
        this.refreshingAvatar = false
      }))
  }
}

const channelStore = new ChannelStore()

export default channelStore



// WEBPACK FOOTER //
// ./src/channel/channel-store.js